/**
 * Gets a handle to the photo directory in the Origin Private File System.
 * Creates the directory if it does not exist.
 * @param {string} directory - The path to the directory to get or create.
 * @returns {Promise<FileSystemDirectoryHandle>}
 */
async function getOrCreateFileDirectory(directory) {
  const root = await navigator.storage.getDirectory();
  const photoDirectoryHandle = await root.getDirectoryHandle(directory, { create: true });
  return photoDirectoryHandle;
}

/**
 * Stores photo data in the Origin Private File System.
 * @param {string} key - The key to associate with the stored photo data.
 * @param {Blob} file - The photo data to be stored.
 * @returns {Promise<void>}
 */
async function storeFile(directory, key, file) {
  const photoDirectoryHandle = await getOrCreateFileDirectory(directory);
  const fileHandle = await photoDirectoryHandle.getFileHandle(key, { create: true });
  const stream = await fileHandle.createWritable();
  await stream.write(file);
  await stream.close();
}

/**
 * Removes photo data from the Origin Private File System.
 * @param {string} key - The key associated with the photo data to be removed.
 * @returns {Promise<void>}
 */
async function removeFile(directory, key) {
  const photoDirectoryHandle = await getOrCreateFileDirectory(directory);
  await photoDirectoryHandle.removeEntry(key);
}

/**
 * Retrieves photo data from the Origin Private File System.
 * @param {string} key
 * @returns {Promise<Blob>} The retrieved photo data.
 */
async function getFile(directory, key) {
  const photoDirectoryHandle = await getOrCreateFileDirectory(directory);
  const fileHandle = await photoDirectoryHandle.getFileHandle(key);
  const file = await fileHandle.getFile();
  return file;
}

export { storeFile, removeFile, getFile };
